import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../../utils/cn';

// Filterable dropdown for picking one record out of a list by name.
// Replaces native <select>, whose built-in type-ahead jumps to (and, when
// controlled, immediately commits) whichever option starts with the typed
// letter — so typing "a" silently selects the first "A…" entry instead of
// narrowing the list, which reads as "filtering is broken".
export default function SearchableSelect({ value, onChange, options, placeholder = 'Search…', className }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const selected = options.find(o => o._id === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => (o.name || '').toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          'w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none flex items-center justify-between gap-2 text-left',
          !selected && 'text-[#706E6B]',
          className
        )}
      >
        <span className={selected ? '' : 'text-[#706E6B]'}>{selected ? selected.name : placeholder}</span>
        <span className="flex items-center gap-1 shrink-0">
          {selected && (
            <X
              size={13}
              className="text-[#706E6B] hover:text-[#080707]"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
            />
          )}
          <ChevronDown size={14} className="text-[#706E6B]" />
        </span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#D8DDE6] rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] max-h-56 overflow-y-auto">
          <input
            autoFocus
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="w-full h-9 px-3 border-b border-[#C9C9C9] text-[13px] outline-none sticky top-0 bg-white"
          />
          <div
            onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
            className="px-3 py-2 text-[13px] text-[#706E6B] hover:bg-[#F3F2F2] cursor-pointer"
          >
            None
          </div>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-[13px] text-[#706E6B]">No matches</div>
          )}
          {filtered.map(o => (
            <div
              key={o._id}
              onClick={() => { onChange(o._id); setOpen(false); setQuery(''); }}
              className={cn(
                'px-3 py-2 text-[13px] hover:bg-[#F3F2F2] cursor-pointer',
                o._id === value && 'bg-[#EAF4FE] font-medium'
              )}
            >
              {o.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
